import { useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  MenuItem,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Delete as RemoveIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import MaterialReactTable from "material-react-table";
import { useAuthUser } from "react-auth-kit";
import { ViewDevice } from "../../DeviceComponents";
import { resolveEffectiveOrgId } from "../../DeviceComponents/deviceFormUtils";
import { EmptyPaper } from "../../LoadingComponents";
import ConfirmDialog from "../../ConfirmDialog/ConfirmDialog";
import AddKitDevicesDialog from "../AddKitDevicesDialog/AddKitDevicesDialog";
import { usePutKitDevices } from "../useKitQueries";
import { kitDeviceIdsFromList } from "../kitDeviceUtils";
import columns from "../../PatientComponents/DispositiviPatient/DispositiviColumns";

function KitDevicesTable({ kitId, orgId, devices = [] }) {
  const authUser = useAuthUser();
  const canManageKitDevices = [1, 2, 3].includes(authUser()?.role);
  const effectiveOrgId = resolveEffectiveOrgId(authUser, orgId);

  const putKitDevices = usePutKitDevices(kitId);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [openViewDevice, setOpenViewDevice] = useState(false);
  const [deviceToRemove, setDeviceToRemove] = useState(null);
  const [openAddDevices, setOpenAddDevices] = useState(false);
  const openInEditMode = useRef(false);

  const handleOpenViewDevice = (deviceId) => {
    setSelectedDevice(deviceId);
    setOpenViewDevice(true);
  };

  const handleConfirmRemove = () => {
    if (!deviceToRemove) return;
    const device_ids = kitDeviceIdsFromList(devices).filter(
      (id) => id !== Number(deviceToRemove.id)
    );
    putKitDevices.mutate({ device_ids }, { onSuccess: () => setDeviceToRemove(null) });
  };

  const currentDeviceIds = kitDeviceIdsFromList(devices);

  return (
    <>
      <ConfirmDialog
        open={deviceToRemove !== null}
        title="Rimuovi strumento dal kit"
        message={`Rimuovere "${deviceToRemove?.name ?? ""}" da questo kit? Il dispositivo non verrà eliminato.`}
        confirmLabel="Rimuovi"
        onCancel={() => setDeviceToRemove(null)}
        onConfirm={handleConfirmRemove}
      />

      <AddKitDevicesDialog
        open={openAddDevices}
        kitId={kitId}
        orgId={effectiveOrgId}
        currentDeviceIds={currentDeviceIds}
        onClose={() => setOpenAddDevices(false)}
      />

      <Dialog open={openViewDevice} fullScreen>
        <ViewDevice
          device_id={selectedDevice}
          closeModal={() => setOpenViewDevice(false)}
          _editMode={false}
        />
      </Dialog>

      {canManageKitDevices && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDevices(true)}
            disabled={effectiveOrgId == null}
          >
            Aggiungi strumenti
          </Button>
        </Box>
      )}

      {devices.length === 0 ? (
        <EmptyPaper message="Nessuno strumento associato a questo kit" />
      ) : (
        <MaterialReactTable
          columns={columns}
          data={devices}
          enableColumnActions={false}
          enableColumnFilters={false}
          enablePagination={devices.length > 10}
          enableSorting={false}
          enableBottomToolbar={devices.length > 10}
          enableTopToolbar={false}
          muiTableBodyRowProps={{ hover: false }}
          muiTablePaperProps={{ elevation: 0 }}
          enableRowActions
          displayColumnDefOptions={{
            "mrt-row-actions": { header: "Azioni" },
          }}
          positionActionsColumn="last"
          renderRowActionMenuItems={({ closeMenu, row }) => {
            const items = [
              <MenuItem
                key="view"
                onClick={() => {
                  openInEditMode.current = false;
                  handleOpenViewDevice(row.original.id);
                  closeMenu();
                }}
              >
                <IconButton>
                  <ViewIcon color="primary" />
                </IconButton>
                Visualizza
              </MenuItem>,
            ];

            if (canManageKitDevices) {
              items.push(
                <MenuItem
                  key="remove"
                  onClick={() => {
                    setDeviceToRemove(row.original);
                    closeMenu();
                  }}
                >
                  <IconButton>
                    <RemoveIcon color="error" />
                  </IconButton>
                  Rimuovi dal kit
                </MenuItem>
              );
            }

            return items;
          }}
        />
      )}
    </>
  );
}

export default KitDevicesTable;
